// plugins/tinymce.client.js
export default defineNuxtPlugin(async () => {
  if (typeof window !== 'undefined') {
    window.tinymce = window.tinymce || {};
    window.tinymce.defaultSettings = window.tinymce.defaultSettings || {};
    window.tinymce.defaultSettings.license_key = 'gpl';
    window.tinymce.license_key = 'gpl';
  }
  if (process.client) {
    // Store original addEventListener
    const originalAddEventListener = EventTarget.prototype.addEventListener;

    // Override addEventListener globally to make touch events passive
    EventTarget.prototype.addEventListener = function (
      type,
      listener,
      options
    ) {
      // Only modify touch events
      if (
        type === 'touchstart' ||
        type === 'touchmove' ||
        type === 'touchend'
      ) {
        // Convert options to object format and add passive: true
        if (typeof options === 'boolean') {
          options = { capture: options, passive: true };
        } else if (typeof options === 'object' && options !== null) {
          options = { ...options, passive: true };
        } else {
          options = { passive: true };
        }
      }
      return originalAddEventListener.call(this, type, listener, options);
    };

    // Set license key
    window.tinymce = window.tinymce || {};
    window.tinymce.license_key = 'gpl';
    window.TINYMCE_LICENSE_KEY = 'gpl';

    try {
      // Check if we're in development or production
      const isDevelopment = process.env.NODE_ENV === 'development';

      if (isDevelopment) {
        await loadTinyMCELocally();
      } else {
        await loadTinyMCEFromCDN();
      }

      // Add a global flag to indicate TinyMCE is ready
      window.tinymceReady = true;

      // Set global configuration for @tinymce/tinymce-vue
      setupTinyMCEGlobalConfig(isDevelopment);
    } catch (error) {
      console.error('Error loading TinyMCE:', error);
      window.tinymceError = error;

      // Try fallback approach
      try {
        const fallbackToDevelopment = process.env.NODE_ENV !== 'development';
        if (fallbackToDevelopment) {
          await loadTinyMCELocally();
        } else {
          await loadTinyMCEFromCDN();
        }
        window.tinymceReady = true;
        setupTinyMCEGlobalConfig(!fallbackToDevelopment);
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
        // Restore original addEventListener on error
        EventTarget.prototype.addEventListener = originalAddEventListener;
      }
    }
  }
});

// Function to setup global TinyMCE configuration
function setupTinyMCEGlobalConfig(isDevelopment) {
  if (!window.tinymce) return;

  // Set global defaults that will be used by @tinymce/tinymce-vue
  window.tinymce.defaultSettings = {
    license_key: 'gpl',
    ...(isDevelopment
      ? {}
      : {
          // For production, ensure CDN paths are used
          base_url: 'https://cdnjs.cloudflare.com/ajax/libs/tinymce/6.8.2',
          suffix: '.min',
        }),
  };
}

// Function to load TinyMCE from CDN
async function loadTinyMCEFromCDN() {
  return new Promise((resolve, reject) => {
    // Check if TinyMCE is already loaded
    if (window.tinymce && window.tinymce.init) {
      window.tinymce.license_key = 'gpl';
      resolve();
      return;
    }

    // Load TinyMCE from CDN
    const script = document.createElement('script');
    script.src =
      'https://cdnjs.cloudflare.com/ajax/libs/tinymce/6.8.2/tinymce.min.js';
    script.onload = () => {
      // Set license key and base URL after loading
      if (window.tinymce) {
        window.tinymce.license_key = 'gpl';
        window.TINYMCE_LICENSE_KEY = 'gpl';

        // Set correct base URL for CDN resources
        window.tinymce.baseURL =
          'https://cdnjs.cloudflare.com/ajax/libs/tinymce/6.8.2';
        window.tinymce.suffix = '.min';

        resolve();
      } else {
        reject(new Error('TinyMCE not available after CDN load'));
      }
    };
    script.onerror = () => {
      reject(new Error('Failed to load TinyMCE from CDN'));
    };
    document.head.appendChild(script);
  });
}

// Function to load TinyMCE locally (development)
async function loadTinyMCELocally() {
  try {
    // Load TinyMCE core
    await import('tinymce/tinymce');

    // Wait for TinyMCE to be ready
    await new Promise((resolve, reject) => {
      let attempts = 0;
      const maxAttempts = 100;

      const checkTinyMCE = () => {
        attempts++;
        if (window.tinymce && window.tinymce.IconManager) {
          resolve();
        } else if (attempts >= maxAttempts) {
          console.error(
            'TinyMCE failed to load after',
            maxAttempts,
            'attempts'
          );
          reject(new Error('TinyMCE timeout'));
        } else {
          setTimeout(checkTinyMCE, 10);
        }
      };
      checkTinyMCE();
    });

    // Set license key again
    window.tinymce.license_key = 'gpl';
    window.tinymce.defaultSettings = window.tinymce.defaultSettings || {};
    window.tinymce.defaultSettings.license_key = 'gpl';

    // Load theme and model
    await import('tinymce/themes/silver');
    await import('tinymce/models/dom');

    // Load icons
    await import('tinymce/icons/default');

    // Load plugins
    await Promise.all([
      import('tinymce/plugins/lists'),
      import('tinymce/plugins/link'),
      import('tinymce/plugins/media'),
      import('tinymce/plugins/image'),
      import('tinymce/plugins/charmap'),
      import('tinymce/plugins/preview'),
      import('tinymce/plugins/anchor'),
      import('tinymce/plugins/autolink'),
      import('tinymce/plugins/advlist'),
      import('tinymce/plugins/code'),
      import('tinymce/plugins/autoresize'),
    ]);
  } catch (error) {
    console.error('Error in loadTinyMCELocally:', error);
    throw error;
  }
}
